import type { QuoteResult } from 'components/quoting/types'
import { publicPlans } from 'rails_routes'
import { calculateAgeAtDate, fullStateNameFromAbbreviation } from 'utils'
import { type QuotingEmployee, type QuotingQuote } from 'utils/web_api.schemas'

// Utility functions for extracting employee data for plan queries
export const getEmployeeAges = (employee: QuotingEmployee): number[] => {
  const nonSelfDependents = (employee.dependents || []).filter((dependent) => dependent.relationship !== 'self')

  return [
    calculateAgeAtDate(employee.date_of_birth, new Date()),
    ...nonSelfDependents.map((dependent) => {
      if (dependent.date_of_birth) {
        return calculateAgeAtDate(dependent.date_of_birth, new Date())
      } else if (dependent.relationship === 'spouse') {
        return calculateAgeAtDate(employee.date_of_birth, new Date())
      } else if (dependent.relationship === 'child') {
        return 12
      } else {
        throw new Error(`Cannot calculate age for dependent: ${dependent.toString()}`)
      }
    })
  ]
}

export const getEmployeeRelationships = (employee: QuotingEmployee): ('self' | 'spouse' | 'child')[] => {
  const nonSelfDependents = (employee.dependents || []).filter((dependent) => dependent.relationship !== 'self')

  return [
    'self',
    ...nonSelfDependents.map(d => d.relationship as 'self' | 'spouse' | 'child'),
  ]
}

export const getEmployeeState = (employee: QuotingEmployee): string => {
  return employee.rating_area_code?.substring(0, 2) || ''
}

export const getQuotePlanYear = (quote?: QuotingQuote) => {
  if (!quote?.coverage_start_date) {
    return new Date().getFullYear()
  }
  // Use the same pattern as other date utilities in the codebase to avoid timezone issues
  const date = new Date(quote.coverage_start_date + 'T00:00:00')
  return date.getFullYear()
}

export const getEmployeePlanParams = (employee: QuotingEmployee, allowance?: number) => {
  const ages = getEmployeeAges(employee)
  const relationships = getEmployeeRelationships(employee)

  return {
    zip: employee.zip,
    state: getEmployeeState(employee),
    ages: JSON.stringify(ages),
    relationships: JSON.stringify(relationships),
    allowance: allowance?.toString() ?? '',
  }
}

export const plansLink = (employee: QuotingEmployee, allowance?: number, useProjectedPremiums?: boolean, year?: number, preliminary?: boolean) => {
  const params = getEmployeePlanParams(employee, allowance)
  const queryParams: Record<string, string> = {
    zip: params.zip,
    state: params.state,
    ages: params.ages,
    relationships: params.relationships,
    allowance: params.allowance?.toString() ?? '',
    preliminary: preliminary ? 'true' : 'false',
    year: year?.toString() ?? '',
    use_projected_premiums: useProjectedPremiums ? 'true' : 'false',
  }

  return publicPlans.index({ query: queryParams })
}

export const planLink = (planId: string, premium?: number) => {
  const params = {
    plan_id: planId,
    ...(premium ? { query: { premium: premium.toFixed(2) } } : {}),
  }
  return publicPlans.detail(params)
}

// ----------------------- //
// CUMULATIVE CALCULATIONS //
// ----------------------- //

export const totalCumulativeAllowanceByState = (quoteResults: QuoteResult[], {
  employerEmployeeContribution,
  employerSpouseContribution,
  employerChildContribution
}: {
  employerEmployeeContribution: number,
  employerSpouseContribution: number,
  employerChildContribution: number
}) => {
  const allowanceByState: Record<string, number> = {}
  quoteResults.forEach((result) => {
    const state = fullStateNameFromAbbreviation(result.rating_area_code.substring(0, 2))
    if (!state) throw new Error(`State not found for ${result.rating_area_code}`)
    const allowance = calculateEmployeeMonthlyAllowanceForEmployeeAndDependents(result, {
      employerEmployeeContribution,
      employerSpouseContribution,
      employerChildContribution
    })
    allowanceByState[state] = (allowanceByState[state] || 0) + allowance
  })
  return allowanceByState
}

// ------------------------ //
// INDIVIDUAL CALCULATIONS //
// ----------------------- //

export const ESTIMATED_SPOUSE_PLAN_PRICE_MULTIPLIER = 1
export const ESTIMATED_CHILD_PLAN_PRICE_MULTIPLIER = 0.5

const MAX_DEPENDENT_COUNT = 3

// used to calculate the total cost for the "benchmarked" plan (ie: take out the contribution percentages from the calculations)
export const calculateMonthlyPriceForEmployeeAndDependentsForBenchmarkedPlan = (employeeResult: QuoteResult) => {
  const basePrice = employeeResult.employee_allowance ?? 0
  let price = basePrice

  const numDependents = employeeResult.dependents.length
  if (numDependents > 0) {
    // first dependent is always a spouse
    price += basePrice * ESTIMATED_SPOUSE_PLAN_PRICE_MULTIPLIER
  }
  if (numDependents > 1) {
    // additional dependents are children
    price += basePrice * ESTIMATED_CHILD_PLAN_PRICE_MULTIPLIER * Math.min(numDependents - 1, MAX_DEPENDENT_COUNT)
  }

  return price
}

export const employeesCurrentPlanCost = (employeeResult: QuoteResult): number | null => {
  if (!employeeResult.employer_medical_contribution || !employeeResult.employee_medical_contribution) return null
  return Number(employeeResult.employer_medical_contribution) + Number(employeeResult.employee_medical_contribution)
}

export const calculateEmployeeMonthlyAllowanceForEmployeeOnly = (employeeResult: QuoteResult, employerEmployeeContribution: number) => {
  return (employeeResult.employee_allowance ?? 0) * employerEmployeeContribution
}

// we legally cannot determine allowances based on type of dependent, so we just check the count of dependents, not the type
// this logic mimics the ICHRA::GetAllowanceAmount operation
export const calculateDependentCoveragePortionOfEmployeeMonthlyAllowance = (employeeResult: QuoteResult, employerSpouseContribution: number, employerChildContribution: number) => {
  let dependentAllowance = 0

  const numDependents = employeeResult.dependents.length
  if (numDependents > 0) {
    // first dependent is always a spouse
    dependentAllowance += (employeeResult.employee_allowance ?? 0) * employerSpouseContribution * ESTIMATED_SPOUSE_PLAN_PRICE_MULTIPLIER
  }

  if (numDependents > 1) {
    // additional dependents are children
    const numAdditionalDependents = Math.min(numDependents - 1, MAX_DEPENDENT_COUNT)
    dependentAllowance += (employeeResult.employee_allowance ?? 0) * employerChildContribution * ESTIMATED_CHILD_PLAN_PRICE_MULTIPLIER * numAdditionalDependents
  }

  return dependentAllowance
}

export const calculateEmployeeMonthlyAllowanceForEmployeeAndDependents = (
  employeeResult: QuoteResult, {
    employerEmployeeContribution,
    employerSpouseContribution,
    employerChildContribution
  }: {
    employerEmployeeContribution: number,
    employerSpouseContribution: number,
    employerChildContribution: number
  }
) => {
  return calculateEmployeeMonthlyAllowanceForEmployeeOnly(employeeResult, employerEmployeeContribution) +
    calculateDependentCoveragePortionOfEmployeeMonthlyAllowance(employeeResult, employerSpouseContribution, employerChildContribution)
}

export const calculateMonthlyEmployeeOutOfPocketContribution = (
  employeeResult: QuoteResult, {
    employerEmployeeContribution,
    employerSpouseContribution,
    employerChildContribution
  }: {
    employerEmployeeContribution: number,
    employerSpouseContribution: number,
    employerChildContribution: number
  }
) => {
  const allowanceAmount = calculateEmployeeMonthlyAllowanceForEmployeeAndDependents(employeeResult, { employerEmployeeContribution, employerSpouseContribution, employerChildContribution })
  const planPrice = calculateMonthlyPriceForEmployeeAndDependentsForBenchmarkedPlan(employeeResult)
  return Math.max(planPrice - allowanceAmount, 0)
}

export const planIsAffordable = (employeeResult: QuoteResult, {
  employerEmployeeContribution,
}: {
  employerEmployeeContribution: number,
}) => (
  calculateEmployeeMonthlyAllowanceForEmployeeOnly(employeeResult, employerEmployeeContribution) >= employeeResult.min_employer_contribution
)

export const flatAllowanceIsAffordable = (employeeResult: QuoteResult, employerCost: number) => {
  return employerCost >= employeeResult.min_employer_contribution
}

export const minAffordableAllowanceForEmployee = (employeeResult: QuoteResult): number | undefined => {
  return employeeResult.min_employer_contribution
}

// Default max employee contribution percentage for 2025
const DEFAULT_MAX_EMPLOYEE_CONTRIBUTION_PERCENTAGE = 9.02

export const formatEmployeeContributionPercentage = (
  maxEmployeeContributionPercentage: number | null | undefined
): string => {
  // Fall back to 2025 in case the quote doesn't have a max employee contribution percentage
  // This is basically just to ensure rendering doesn't fail
  if (maxEmployeeContributionPercentage == null) {
    return DEFAULT_MAX_EMPLOYEE_CONTRIBUTION_PERCENTAGE.toFixed(2)
  }

  // Convert from decimal to percentage and format to 2 decimal places
  return (maxEmployeeContributionPercentage * 100).toFixed(2)
}
